import { Component, ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

// React's error-boundary API still requires a class component (no hook equivalent).
// Scoped here rather than in components/ui/ so a single panel-render failure
// degrades to an inline message instead of unmounting the whole app.

interface Props {
  children: ReactNode
  label?: string
}

interface State {
  error: Error | null
}

export class PanelErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    console.error('[PanelErrorBoundary] render failed:', error, info.componentStack)
  }

  reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-sm w-full rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex items-center gap-2 text-destructive mb-2">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">
              {this.props.label ?? 'Panel'} failed to render
            </span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            {error.message || 'Unknown error'}
          </p>
          <button
            onClick={this.reset}
            className="text-xs px-2 py-1 rounded-md bg-background border border-border hover:bg-muted transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }
}
